import { useState, useRef, useEffect } from 'react';
import AgoraRTC, { createClient } from 'facephi-agora-rtc-react';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

var WsMessage;
(function (WsMessage) {
    WsMessage["CREDENTIALS_REQUEST"] = "CREDENTIALS_REQUEST";
    WsMessage["CREDENTIALS_RESPONSE"] = "CREDENTIALS_RESPONSE";
    WsMessage["WAITING_CONFIRMATION"] = "WAITING_CONFIRMATION";
    WsMessage["IDENTIFICATION_STARTED"] = "IDENTIFICATION_STARTED";
    WsMessage["STOP"] = "STOP";
    WsMessage["IDENTIFICATION_STOPPED"] = "IDENTIFICATION_STOPPED";
})(WsMessage || (WsMessage = {}));
var useRecordingWs = function (_a) {
    var apiUrl = _a.apiUrl, apiKey = _a.apiKey, operationId = _a.operationId, tenantId = _a.tenantId;
    var _b = useState(false), enabled = _b[0], setEnabled = _b[1];
    var _c = useState(false), error = _c[0], setError = _c[1];
    var _d = useState(false), recording = _d[0], setRecording = _d[1];
    var _e = useState(false), loading = _e[0], setLoading = _e[1];
    var _f = useState(), credentials = _f[0], setCredentials = _f[1];
    var websocket = useRef();
    useEffect(function () {
        if (enabled && operationId) {
            var ws = new WebSocket(apiUrl);
            ws.onerror = function () { return setError(true); };
            ws.onmessage = function (lastMessage) { return handleMessage(lastMessage.data); };
            ws.onopen = function () { return sendCredentials(); };
            websocket.current = ws;
        }
        return function () {
            if (websocket.current &&
                websocket.current.readyState === websocket.current.OPEN) {
                closeWebSocket();
            }
            websocket.current = null;
        };
    }, [enabled, operationId]);
    useEffect(function () {
        if (error) {
            closeWebSocket();
        }
    }, [error]);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    var sendWs = function (data) {
        var _a, _b;
        if (websocket.current &&
            ((_a = websocket.current) === null || _a === void 0 ? void 0 : _a.readyState) === ((_b = websocket.current) === null || _b === void 0 ? void 0 : _b.OPEN)) {
            websocket.current.send(JSON.stringify(data));
        }
    };
    var handleMessage = function (data) {
        if (data) {
            var message = JSON.parse(data);
            switch (message.type) {
                case WsMessage.CREDENTIALS_RESPONSE:
                    setCredentials(message.credentials);
                    break;
                case WsMessage.IDENTIFICATION_STARTED:
                    setLoading(false);
                    setRecording(true);
                    break;
            }
        }
    };
    var sendCredentials = function () {
        sendWs({
            type: WsMessage.CREDENTIALS_REQUEST,
            apiKey: apiKey,
            sessionId: operationId,
        });
    };
    useEffect(function () {
        var timeout;
        if (loading) {
            timeout = setTimeout(function () {
                setError(true);
            }, 15000);
        }
        return function () {
            timeout && clearTimeout(timeout);
        };
    }, [loading]);
    var getCredentials = function () {
        setEnabled(true);
        setLoading(true);
    };
    var startRecord = function () {
        sendWs({
            type: WsMessage.WAITING_CONFIRMATION,
            sessionId: operationId,
            tenantId: tenantId,
        });
    };
    var stopRecord = function () {
        sendWs({
            type: WsMessage.STOP,
            apiKey: apiKey,
            sessionId: operationId,
            tenantId: tenantId,
        });
    };
    var resetVariables = function () {
        setError(false);
        setRecording(false);
        setLoading(false);
        setCredentials(undefined);
    };
    var closeWebSocket = function () {
        if (websocket.current) {
            stopRecord();
            websocket.current.close();
        }
        resetVariables();
        setEnabled(false);
    };
    return {
        error: error,
        recording: recording,
        credentials: credentials,
        getCredentials: getCredentials,
        startRecord: startRecord,
        stopRecord: closeWebSocket,
    };
};

AgoraRTC.setLogLevel(4 /* NONE */);
var useClient = createClient({
    mode: 'rtc',
    codec: 'vp8',
});
var useVideo = function (_a) {
    var baseUrl = _a.baseUrl, apiKey = _a.apiKey, operationId = _a.operationId, tenantId = _a.tenantId, autoStart = _a.autoStart, audio = _a.audio, _b = _a.video, video = _b === void 0 ? true : _b, setChannel = _a.setChannel;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    var tracksRef = useRef([]);
    var _c = useState(false), error = _c[0], setError = _c[1];
    var agoraClient = useClient();
    var started = useRef(false);
    var _d = useRecordingWs({
        apiUrl: "wss://".concat(baseUrl, "/identification"),
        apiKey: apiKey,
        operationId: operationId,
        tenantId: tenantId,
    }), errorWs = _d.error, credentials = _d.credentials, getCredentials = _d.getCredentials, recording = _d.recording, startRecord = _d.startRecord, stopRecord = _d.stopRecord;
    useEffect(function () {
        if (autoStart && operationId && tenantId) {
            handleStart();
        }
        return function () {
            handleStop();
        };
    }, [operationId, tenantId, autoStart]);
    useEffect(function () {
        if (credentials) {
            joinToChannel();
        }
    }, [credentials]);
    var handleMuted = function () {
        var _a, _b;
        if (!audio) {
            (_a = tracksRef.current[0]) === null || _a === void 0 ? void 0 : _a.setMuted(true);
        }
        if (!video) {
            (_b = tracksRef.current[1]) === null || _b === void 0 ? void 0 : _b.setMuted(true);
        }
    };
    var onChangeChannel = function () {
        var channel = {
            audio: tracksRef.current[0] || null,
            video: tracksRef.current[1] || null,
        };
        var hasTrack = Object.values(channel).some(function (item) { return item; });
        setChannel === null || setChannel === void 0 ? void 0 : setChannel(hasTrack ? channel : undefined);
    };
    var handleStart = function () { return __awaiter(void 0, void 0, void 0, function () {
        var _a, audioTrack, videoTrack, error_1;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    started.current = true;
                    setError(false);
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, AgoraRTC.createMicrophoneAndCameraTracks(undefined, {
                            encoderConfig: '720p_2',
                            facingMode: 'environment',
                        })];
                case 2:
                    _a = _b.sent(), audioTrack = _a[0], videoTrack = _a[1];
                    tracksRef.current = [audioTrack, videoTrack];
                    handleMuted();
                    onChangeChannel();
                    getCredentials();
                    return [3 /*break*/, 4];
                case 3:
                    error_1 = _b.sent();
                    console.log(error_1);
                    setError(true);
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); };
    var joinToChannel = function () { return __awaiter(void 0, void 0, void 0, function () {
        var _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    if (!credentials) return [3 /*break*/, 4];
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, agoraClient.join(credentials.appId, credentials.channel, credentials.cameraToken, credentials.cameraUid)];
                case 2:
                    _c.sent();
                    (_b = tracksRef.current) === null || _b === void 0 ? void 0 : _b.forEach(function (item) { return __awaiter(void 0, void 0, void 0, function () {
                        return __generator(this, function (_a) {
                            switch (_a.label) {
                                case 0:
                                    if (!item) return [3 /*break*/, 2];
                                    return [4 /*yield*/, agoraClient.publish(item)];
                                case 1:
                                    _a.sent();
                                    _a.label = 2;
                                case 2: return [2 /*return*/];
                            }
                        });
                    }); });
                    startRecord();
                    return [3 /*break*/, 4];
                case 3:
                    _c.sent();
                    setError(true);
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); };
    var closeTracks = function () {
        var _a;
        started.current = false;
        (_a = tracksRef.current) === null || _a === void 0 ? void 0 : _a.forEach(function (item) { return __awaiter(void 0, void 0, void 0, function () {
            return __generator(this, function (_a) {
                item === null || item === void 0 ? void 0 : item.stop();
                item === null || item === void 0 ? void 0 : item.close();
                return [2 /*return*/];
            });
        }); });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        tracksRef.current = [];
        onChangeChannel();
        if (agoraClient.connectionState === 'CONNECTED') {
            agoraClient.unpublish();
            agoraClient.leave();
        }
    };
    var handleStop = function () {
        stopRecord();
        closeTracks();
    };
    useEffect(function () {
        if (errorWs) {
            closeTracks();
        }
    }, [errorWs]);
    var getCameras = function () { return __awaiter(void 0, void 0, void 0, function () {
        var cameras;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, AgoraRTC.getCameras()];
                case 1:
                    cameras = _a.sent();
                    return [2 /*return*/, cameras.map(function (_a) {
                            var deviceId = _a.deviceId, label = _a.label;
                            return ({
                                deviceId: deviceId,
                                label: label,
                            });
                        })];
            }
        });
    }); };
    var getTypeCameras = function (facingMode) { return __awaiter(void 0, void 0, void 0, function () {
        var cameras;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, getCameras()];
                case 1:
                    cameras = _a.sent();
                    return [2 /*return*/, cameras.reduce(function (acc, item) { return __awaiter(void 0, void 0, void 0, function () {
                            var rest, camera;
                            return __generator(this, function (_b) {
                                switch (_b.label) {
                                    case 0: return [4 /*yield*/, acc];
                                    case 1:
                                        rest = _b.sent();
                                        _b.label = 2;
                                    case 2:
                                        _b.trys.push([2, 4, , 5]);
                                        return [4 /*yield*/, navigator.mediaDevices.getUserMedia({
                                                video: {
                                                    facingMode: { exact: facingMode },
                                                    deviceId: { exact: item.deviceId },
                                                },
                                            })];
                                    case 3:
                                        camera = _b.sent();
                                        if (camera) {
                                            rest.push(item.deviceId);
                                        }
                                        return [3 /*break*/, 5];
                                    case 4:
                                        _b.sent();
                                        return [3 /*break*/, 5];
                                    case 5: return [2 /*return*/, rest];
                                }
                            });
                        }); }, Promise.resolve([]))];
            }
        });
    }); };
    var setCamera = function (facingMode) { return __awaiter(void 0, void 0, void 0, function () {
        var availableCameras;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!started.current) return [3 /*break*/, 3];
                    return [4 /*yield*/, getTypeCameras(facingMode)];
                case 1:
                    availableCameras = _b.sent();
                    if (!(availableCameras === null || availableCameras === void 0 ? void 0 : availableCameras.length)) return [3 /*break*/, 3];
                    return [4 /*yield*/, ((_a = tracksRef.current[1]) === null || _a === void 0 ? void 0 : _a.setDevice(availableCameras[0]))];
                case 2:
                    _b.sent();
                    onChangeChannel();
                    _b.label = 3;
                case 3: return [2 /*return*/];
            }
        });
    }); };
    return {
        error: error || errorWs,
        recording: recording,
        handleStop: handleStop,
        getCameras: getCameras,
        getTypeCameras: getTypeCameras,
        handleStart: handleStart,
        setCameraRear: function () { return setCamera('environment'); },
        setCameraFront: function () { return setCamera('user'); },
    };
};

export { useVideo };
